import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';

const POLL_INTERVAL = 10000;

const statusColor = { in_service: '#3B6D11', en_route: '#854F0B', idle: '#888780' };
const statusLabel = { in_service: 'In service', en_route: 'En route', idle: 'Idle' };
const statusBg    = { in_service: '#EAF3DE',   en_route: '#FEF3C7', idle: '#F3F4F6' };

export default function LiveTracking() {
  const [techs, setTechs]           = useState([]);
  const [selected, setSelected]     = useState(null);
  const [history, setHistory]       = useState([]);
  const [routeData, setRouteData]   = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [selectedTech, setSelectedTech] = useState(null);
  const [routeDate, setRouteDate]   = useState(new Date().toISOString().split('T')[0]);
  const [view, setView]             = useState('live'); // live | history
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [allTechs, setAllTechs]       = useState([]);
  const mapRef                      = useRef(null);
  const leafletMap                  = useRef(null);
  const markers                     = useRef({});
  const historyLine                 = useRef(null);
  const stopMarkers                  = useRef([]);
  const intervalRef                 = useRef(null);

  useEffect(() => {
    api.get('/users?role=technician,supervisor,manager&is_active=true').then(r => {
      setAllTechs(r.data.data || r.data.users || r.data || []);
    }).catch(e => console.error(e));
  }, []);

  const fetchLive = useCallback(async () => {
    try {
      const r = await api.get('/tracking/live');
      setTechs(r.data.data || []);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (e) { console.error(e); setLoading(false); }
  }, []);

  const fetchRoute = async (userId, date) => {
    setRouteLoading(true);
    setRouteData(null);
    try {
      const r = await api.get(`/tracking/route/${userId}?date=${date}`);
      setRouteData(r.data);
      // Draw route on map
      const L = window.L;
      if (L && leafletMap.current) {
        if (historyLine.current) { historyLine.current.remove(); historyLine.current = null; }
        const pts = (r.data.pings||[]).map(p => [p.lat, p.lng]);
        if (pts.length > 1) {
          historyLine.current = L.polyline(pts, { color:'#2D5C3F', weight:3, opacity:0.7 }).addTo(leafletMap.current);
          leafletMap.current.fitBounds(historyLine.current.getBounds(), { padding:[40,40] });
        }
        // Add stop markers
        stopMarkers.current.forEach(m => m.remove());
        stopMarkers.current = [];
        (r.data.stops||[]).forEach((stop, i) => {
          const icon = L.divIcon({
            className:'',
            html:`<div style="width:28px;height:28px;border-radius:50%;background:${stop.unusual?'#DC2626':'#1A3A2A'};border:2px solid white;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:white">${i+1}</div>`,
            iconSize:[28,28], iconAnchor:[14,14]
          });
          const sm = L.marker([stop.lat, stop.lng], { icon })
            .addTo(leafletMap.current)
            .bindPopup(`<b>${stop.facility_name||'Stop '+(i+1)}</b><br>${stop.duration_min} min${stop.wo_number?'<br>WO: '+stop.wo_number:''}`);
          stopMarkers.current.push(sm);
        });
      }
    } catch(e) { console.error(e); }
    setRouteLoading(false);
  };

  // Init Leaflet map
  useEffect(() => {
    if (leafletMap.current) return;
    const L = window.L;
    if (!L) return;
    const map = L.map(mapRef.current, { center: [40.7128, -74.006], zoom: 11 });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);
    leafletMap.current = map;
  }, []);

  // Update markers on techs change
  useEffect(() => {
    const L = window.L;
    if (!L || !leafletMap.current) return;
    const map = leafletMap.current;

    techs.forEach(tech => {
      const lat = parseFloat(tech.lat);
      const lng = parseFloat(tech.lng);
      const col = statusColor[tech.status] || '#888780';
      const initials = ((tech.first_name||'?')[0] + (tech.last_name||'?')[0]).toUpperCase();
      const icon = L.divIcon({
        className: '',
        html: `<div style="width:36px;height:36px;border-radius:50%;background:${col};border:3px solid white;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:white;box-shadow:0 2px 6px rgba(0,0,0,0.3)">${initials}</div>`,
        iconSize: [36, 36], iconAnchor: [18, 18]
      });
      const isOffline = parseFloat(tech.seconds_ago) > 180;
      const effectiveIcon = isOffline ? L.divIcon({
        className: '',
        html: `<div style="width:36px;height:36px;border-radius:50%;background:#9CA3AF;border:3px solid white;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:white;opacity:0.6">${initials}</div>`,
        iconSize: [36, 36], iconAnchor: [18, 18]
      }) : icon;
      if (markers.current[tech.user_id]) {
        markers.current[tech.user_id].setLatLng([lat, lng]).setIcon(effectiveIcon);
      } else {
        const m = L.marker([lat, lng], { icon: effectiveIcon })
          .addTo(map)
          .on('click', () => setSelected(tech));
        markers.current[tech.user_id] = m;
      }
      markers.current[tech.user_id].bindPopup(
        `<b>${tech.tech_name}</b><br>${statusLabel[tech.status]||tech.status}${tech.wo_number ? '<br>WO: ' + tech.wo_number : ''}${tech.facility_name ? '<br>' + tech.facility_name : ''}`
      );
    });

    // Remove markers for techs no longer in list
    const activeIds = techs.map(t => t.user_id);
    Object.keys(markers.current).forEach(id => {
      if (!activeIds.includes(parseInt(id))) {
        markers.current[id].remove();
        delete markers.current[id];
      }
    });
  }, [techs]);

  // Polling
  useEffect(() => {
    fetchLive();
    intervalRef.current = setInterval(fetchLive, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [fetchLive]);

  // Load history when selected + history view
  useEffect(() => {
    if (!selected || view !== 'history') return;
    const L = window.L;
    api.get(`/tracking/history/${selected.user_id}`).then(r => {
      setHistory(r.data.data || []);
      if (!L || !leafletMap.current) return;
      if (historyLine.current) historyLine.current.remove();
      const pts = (r.data.data || []).map(p => [parseFloat(p.lat), parseFloat(p.lng)]);
      if (pts.length > 1) {
        historyLine.current = L.polyline(pts, { color: '#2D5C3F', weight: 3, opacity: 0.7 }).addTo(leafletMap.current);
        leafletMap.current.fitBounds(historyLine.current.getBounds(), { padding: [40, 40] });
      }
    });
    return () => { if (historyLine.current) { historyLine.current.remove(); historyLine.current = null; } };
  }, [selected, view]);

  // Center map on selected tech
  const centerOnTech = (tech) => {
    setSelected(tech);
    if (leafletMap.current) {
      leafletMap.current.setView([parseFloat(tech.lat), parseFloat(tech.lng)], 14);
      if (markers.current[tech.user_id]) markers.current[tech.user_id].openPopup();
    }
  };

  const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',hour12:true});
  const fmtDuration = (min) => min >= 60 ? `${Math.floor(min/60)}h ${min%60}m` : `${min}m`;

  const secsAgo = (s) => {
    const n = Math.round(parseFloat(s || 0));
    if (n < 60) return `${n}s ago`;
    return `${Math.floor(n/60)}m ago`;
  };

  const inField   = techs.length;
  const inService = techs.filter(t => t.status === 'in_service').length;
  const idle      = techs.filter(t => t.status === 'idle' && parseFloat(t.seconds_ago) > 1200).length;

  return (
    <Layout>
      <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
      <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js" onLoad={() => {
        if (!leafletMap.current && mapRef.current) {
          const L = window.L;
          const map = L.map(mapRef.current, { center: [40.7128, -74.006], zoom: 11 });
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap' }).addTo(map);
          leafletMap.current = map;
        }
      }} />

      <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 64px)', overflow:'hidden' }}>

        {/* Topbar */}
        <div style={{ padding:'14px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', alignItems:'center', background:'white', flexShrink:0 }}>
          <div>
            <h1 style={{ margin:0, fontSize:'18px', fontWeight:700, color:'#1A3A2A' }}>Live field tracking</h1>
            <div style={{ fontSize:'12px', color:'#9CA3AF', marginTop:'2px' }}>
              {lastUpdate ? `Updated ${lastUpdate.toLocaleTimeString()} · polling every 10s` : 'Loading...'}
            </div>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            {['live','history'].map(v => (
              <button key={v} onClick={() => {
                setView(v);
                const L = window.L;
                if (!L || !leafletMap.current) return;
                if (v === 'history') {
                  // Hide live markers
                  Object.values(markers.current).forEach(m => m.remove());
                } else {
                  // Restore live markers and clear route
                  Object.entries(markers.current).forEach(([id, m]) => m.addTo(leafletMap.current));
                  if (historyLine.current) { historyLine.current.remove(); historyLine.current = null; }
                  stopMarkers.current.forEach(m => m.remove());
                  stopMarkers.current = [];
                  setRouteData(null);
                }
              }}
                style={{ padding:'7px 16px', borderRadius:'8px', border:'1px solid #E5E7EB', cursor:'pointer', fontSize:'13px', fontWeight:600,
                  background: view===v ? '#1A3A2A' : 'white', color: view===v ? 'white' : '#374151' }}>
                {v === 'live' ? '📍 Live map' : '📈 Route history'}
              </button>
            ))}
          </div>
        </div>

        {/* KPI strip */}
        <div style={{ display:'flex', borderBottom:'1px solid #E5E7EB', background:'white', flexShrink:0 }}>
          {[
            { label:'In field', val: inField, col:'#1A3A2A' },
            { label:'In service', val: inService, col:'#3B6D11' },
            { label:'Idle >20min', val: idle, col: idle>0 ? '#B45309' : '#9CA3AF' },
            { label:'Last ping', val: lastUpdate ? lastUpdate.toLocaleTimeString() : '—', col:'#374151', sm:true },
          ].map((k,i) => (
            <div key={i} style={{ flex:1, padding:'10px 20px', borderRight:'1px solid #E5E7EB', textAlign:'center' }}>
              <div style={{ fontSize: k.sm?'13px':'22px', fontWeight:700, color:k.col }}>{k.val}</div>
              <div style={{ fontSize:'11px', color:'#9CA3AF', marginTop:'2px' }}>{k.label}</div>
            </div>
          ))}
        </div>

        {/* Main content */}
        <div style={{ display:'flex', flex:1, overflow:'hidden' }}>

          {/* Map */}
          <div style={{ flex:1, position:'relative' }}>
            {loading && (
              <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(255,255,255,0.8)', zIndex:500 }}>
                <div style={{ color:'#9CA3AF', fontSize:'14px' }}>Loading map...</div>
              </div>
            )}
            {!loading && techs.length === 0 && view === 'live' && (
              <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', zIndex:500, pointerEvents:'none' }}>
                <div style={{ background:'white', borderRadius:'12px', padding:'24px 32px', textAlign:'center', border:'1px solid #E5E7EB' }}>
                  <div style={{ fontSize:'32px', marginBottom:'8px' }}>📍</div>
                  <div style={{ fontWeight:700, color:'#374151' }}>No technicians in field</div>
                  <div style={{ fontSize:'13px', color:'#9CA3AF', marginTop:'4px' }}>Locations appear when technicians open the mobile app</div>
                </div>
              </div>
            )}
            <div ref={mapRef} style={{ width:'100%', height:'100%' }} />
          </div>

          {/* Right panel */}
          <div style={{ width:'260px', borderLeft:'1px solid #E5E7EB', display:'flex', flexDirection:'column', overflow:'hidden', background:'white' }}>
            {view === 'history' ? (
              <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
                <div style={{ padding:'10px 12px', borderBottom:'1px solid #E5E7EB' }}>
                  <div style={{ fontSize:'12px', fontWeight:700, color:'#374151', marginBottom:'8px' }}>ROUTE HISTORY</div>
                  <select onChange={e => { setSelectedTech(e.target.value); if(e.target.value) fetchRoute(e.target.value, routeDate); }}
                    style={{ width:'100%', padding:'6px 8px', border:'1px solid #E5E7EB', borderRadius:'6px', fontSize:'12px', marginBottom:'6px' }}>
                    <option value="">Select technician...</option>
                    {allTechs.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
                  </select>
                  <input type="date" value={routeDate} onChange={e => { setRouteDate(e.target.value); if(selectedTech) fetchRoute(selectedTech, e.target.value); }}
                    style={{ width:'100%', padding:'6px 8px', border:'1px solid #E5E7EB', borderRadius:'6px', fontSize:'12px' }} />
                </div>
                <div style={{ flex:1, overflowY:'auto', padding:'10px 12px' }}>
                  {routeLoading && <div style={{ textAlign:'center', padding:'20px', color:'#9CA3AF', fontSize:'12px' }}>Loading route...</div>}
                  {!routeLoading && !routeData && <div style={{ textAlign:'center', padding:'20px', color:'#9CA3AF', fontSize:'12px' }}>Select a technician to view their route</div>}
                  {routeData && (
                    <>
                      <div style={{ background:'#F0F7F4', borderRadius:'8px', padding:'10px', marginBottom:'12px', fontSize:'11px' }}>
                        <div style={{ fontWeight:700, color:'#1A3A2A', marginBottom:'4px' }}>{routeData.tech_name}</div>
                        <div style={{ color:'#6B7280' }}>{routeData.ping_count} pings · {routeData.total_distance_km} km total</div>
                        <div style={{ color:'#6B7280' }}>{routeData.stops?.length || 0} stops detected</div>
                      </div>
                      {(routeData.stops||[]).length === 0 && (
                        <div style={{ textAlign:'center', padding:'20px', color:'#9CA3AF', fontSize:'12px' }}>No stops detected for this day</div>
                      )}
                      {(routeData.stops||[]).map((stop, i) => (
                        <div key={i} style={{ marginBottom:'4px' }}>
                          {i > 0 && stop.travel_from_prev_min !== undefined && (
                            <div style={{ display:'flex', alignItems:'center', gap:'6px', padding:'4px 8px', margin:'2px 0' }}>
                              <div style={{ width:'2px', height:'20px', background: stop.unusual?'#DC2626':'#D1D5DB', margin:'0 auto', marginLeft:'11px' }} />
                              <div style={{ fontSize:'10px', color: stop.unusual?'#DC2626':'#9CA3AF', marginLeft:'8px' }}>
                                {stop.unusual && '⚠ '}{stop.travel_from_prev_min} min · {stop.travel_from_prev_km} km
                              </div>
                            </div>
                          )}
                          <div style={{ border:`1px solid ${stop.unusual?'#FECACA':'#E5E7EB'}`, borderRadius:'8px', padding:'8px 10px',
                            background: stop.unusual?'#FEF2F2':'white' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'3px' }}>
                              <div style={{ width:'20px', height:'20px', borderRadius:'50%', background: stop.unusual?'#DC2626':'#1A3A2A',
                                display:'flex', alignItems:'center', justifyContent:'center', fontSize:'9px', fontWeight:700, color:'white', flexShrink:0 }}>
                                {i+1}
                              </div>
                              <div style={{ fontSize:'11px', fontWeight:700, color:'#1A3A2A', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                {stop.facility_name || stop.address || 'Stop ' + (i+1)}
                              </div>
                            </div>
                            <div style={{ fontSize:'10px', color:'#6B7280', marginLeft:'26px' }}>
                              {fmtTime(stop.arrived_at)} → {fmtTime(stop.departed_at)}
                            </div>
                            <div style={{ fontSize:'10px', color:'#374151', fontWeight:600, marginLeft:'26px' }}>
                              {fmtDuration(stop.duration_min)}
                              {stop.wo_number && <span style={{ color:'#2563EB', marginLeft:'6px' }}>{stop.wo_number}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            ) : (
            <>
            <div style={{ padding:'10px 14px', borderBottom:'1px solid #E5E7EB', fontSize:'12px', fontWeight:700, color:'#374151' }}>
              TECHNICIANS IN FIELD
            </div>
            <div style={{ overflowY:'auto', flex:1 }}>
              {techs.length === 0 && (
                <div style={{ padding:'24px', textAlign:'center', color:'#9CA3AF', fontSize:'12px' }}>No active technicians</div>
              )}
              {techs.map(tech => {
                const col = statusColor[tech.status] || '#888780';
                const isSelected = selected?.user_id === tech.user_id;
                const initials = ((tech.first_name||'?')[0] + (tech.last_name||'?')[0]).toUpperCase();
                const isIdleLong = tech.status === 'idle' && parseFloat(tech.seconds_ago) > 1200;
                return (
                  <div key={tech.user_id} onClick={() => centerOnTech(tech)}
                    style={{ padding:'10px 14px', borderBottom:'1px solid #F3F4F6', cursor:'pointer',
                      background: isSelected ? '#F0F7F4' : 'white' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'5px' }}>
                      <div style={{ width:'28px', height:'28px', borderRadius:'50%', background:col, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', fontWeight:700, color:'white', flexShrink:0 }}>
                        {initials}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:'12px', fontWeight:700, color:'#1A3A2A', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{tech.tech_name}</div>
                        <div style={{ fontSize:'10px', color: parseFloat(tech.seconds_ago)>180 ? '#EF4444' : '#9CA3AF' }}>
                          {parseFloat(tech.seconds_ago)>180 ? '⚠ Inactive · ' : ''}{secsAgo(tech.seconds_ago)}
                        </div>
                      </div>
                      <div style={{ width:'8px', height:'8px', borderRadius:'50%', background: isIdleLong ? '#EF4444' : col, flexShrink:0 }} />
                    </div>
                    <div style={{ fontSize:'10px', padding:'3px 8px', borderRadius:'4px', background:statusBg[tech.status]||'#F3F4F6', color:col, fontWeight:600, display:'inline-block', marginBottom: tech.wo_number ? '4px' : 0 }}>
                      {statusLabel[tech.status] || tech.status}
                    </div>
                    {tech.wo_number && (
                      <div style={{ fontSize:'10px', color:'#6B7280', marginTop:'3px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {tech.wo_number}{tech.facility_name ? ' · ' + tech.facility_name : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
